/**
 * Product analytics: what we measure and what may leave the browser.
 *
 * PostHog and Google Analytics run only with the visitor's consent (see
 * `./analytics.ts`). Events are anonymous. They carry no user, company,
 * e-mail, amount, IBAN, vendor, file name or document content, only the
 * small, fixed properties listed here. Per-user activation is counted from the
 * database instead.
 */

/** Every event the app sends, with the only properties it may carry. */
export interface TrackingEvents {
  /**
   * A new account: the sign-up form was sent, or a Google / Microsoft account
   * accepted the terms for the first time. The plan chosen, or "none", and how.
   */
  signed_up: { plan: string; method: "email" | "google" | "azure" | "oauth" };
  /** The sign-up form was shown (once per page load). */
  signup_form_viewed: Record<string, never>;
  /**
   * A sign-up that did not go out or was refused: `invalid_<field>` for the
   * first field the form rejected, otherwise the Supabase error kind
   * (captchaFailed, signupDisabled, weakPassword, rateLimited, invalidEmail, other).
   */
  signup_failed: { reason: string };
  /** Back from the confirmation e-mail's link. */
  email_confirmed: Record<string, never>;
  /** The company's first Fio sync that brought transactions in. */
  fio_connected: Record<string, never>;
  /** The company's first document (uploaded or e-mailed). */
  first_document: { source: "email" | "upload" };
  /** The company's first payment matched to a document. */
  first_auto_match: Record<string, never>;
  /** A first-run checklist step was used; the step number (1–4). */
  onboarding_step_clicked: { step: number };
  /** The finished first-run checklist was hidden. */
  onboarding_dismissed: Record<string, never>;
  /** A Fio account was saved with its API token. */
  fio_account_added: Record<string, never>;
  /** A GPC/ABO statement import finished. */
  statement_imported: { imported: number; duplicates: number; failed: boolean };
  /** Documents uploaded and read by the AI. */
  documents_uploaded: { count: number; source: "dialog" | "receipts_page" | "capture" };
  /** A document was dropped onto a payment by hand. */
  manual_match_created: { mode: string };
  /** The monthly PDF bundle of receipts was downloaded. */
  receipts_bundle_downloaded: { partial: boolean };
  /** Invoices or clients exported. */
  data_exported: { kind: string; format: string };
  /** A colleague or accountant was invited to the company. */
  member_invited: { role: string };
  /** An invoice was issued in Zettl. */
  invoice_created: Record<string, never>;
  /** Stripe Checkout opened for a plan. */
  checkout_started: { plan: string };
  /**
   * A free tool on the public site produced its output (e.g. the GPC
   * converter's download): which tool and which format. Never the file, its
   * name or any value from it.
   */
  tool_used: { tool: string; output: string };
  /** Zettl was installed on a phone's home screen from the in-app hint (Android). */
  app_installed: Record<string, never>;
  /**
   * The sample company was opened ("Ukázat na ukázkových datech"): from the
   * empty dashboard or the first-run checklist. Nothing about the company.
   */
  demo_opened: { source: "dashboard" | "onboarding" };
  /** Back from the sample company to the real one. */
  demo_exited: Record<string, never>;
}

export type TrackingEvent = keyof TrackingEvents;

const ALLOWED_PROPERTIES: { [E in TrackingEvent]: readonly (keyof TrackingEvents[E])[] } = {
  signed_up: ["plan", "method"],
  signup_form_viewed: [],
  signup_failed: ["reason"],
  email_confirmed: [],
  fio_connected: [],
  first_document: ["source"],
  first_auto_match: [],
  onboarding_step_clicked: ["step"],
  onboarding_dismissed: [],
  fio_account_added: [],
  statement_imported: ["imported", "duplicates", "failed"],
  documents_uploaded: ["count", "source"],
  manual_match_created: ["mode"],
  receipts_bundle_downloaded: ["partial"],
  data_exported: ["kind", "format"],
  member_invited: ["role"],
  invoice_created: [],
  checkout_started: ["plan"],
  tool_used: ["tool", "output"],
  app_installed: [],
  demo_opened: ["source"],
  demo_exited: [],
};

/** Keeps only the listed properties, and only numbers, booleans and short strings. */
export function allowedProperties<E extends TrackingEvent>(
  event: E,
  properties: Partial<TrackingEvents[E]> | undefined
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!properties) return out;
  for (const key of ALLOWED_PROPERTIES[event] as readonly string[]) {
    const value = (properties as Record<string, unknown>)[key];
    if (typeof value === "number" && Number.isFinite(value)) out[key] = value;
    else if (typeof value === "boolean") out[key] = value;
    else if (typeof value === "string" && value.length <= 40) out[key] = value;
  }
  return out;
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * A URL as PostHog may see it: origin and path only, record ids replaced by
 * `:id`, and only `utm_*` / `ref` query parameters kept. Hashes and other
 * parameters can hold sign-in tokens (password reset), OAuth state or
 * `?next=` paths, so they never leave the browser.
 */
export function sanitizeUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return value.replace(/[?#].*$/, "").replace(UUID, ":id");
  }
  const kept = new URLSearchParams();
  url.searchParams.forEach((v, k) => {
    if (/^utm_[a-z]+$/.test(k) || k === "ref") kept.set(k, v);
  });
  const query = kept.toString();
  return `${url.origin}${url.pathname.replace(UUID, ":id")}${query ? `?${query}` : ""}`;
}

/** Event properties PostHog fills in with a URL or path, cleaned before sending. */
const URL_PROPERTY = /(url|referrer|pathname)$/i;

export function sanitizeUrlProperties(
  properties: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!properties) return properties;
  const out: Record<string, unknown> = { ...properties };
  for (const [key, value] of Object.entries(out)) {
    if (typeof value === "string" && URL_PROPERTY.test(key) && value !== "$direct") {
      out[key] = sanitizeUrl(value);
    }
  }
  return out;
}

/**
 * Production hosts only: previews share the production database but not its
 * analytics. zettlapp.com now only redirects, apart from the MCP sign-in.
 */
export const TRACKED_HOSTS: readonly string[] = [
  "zettlapp.com",
  "www.zettlapp.com",
  "zettl.cz",
  "www.zettl.cz",
  "app.zettl.cz",
];

export interface AnalyticsConfig {
  posthogKey: string | null;
  /** PostHog's EU cloud unless VITE_POSTHOG_HOST names another https host (e.g. a proxy). */
  posthogHost: string;
  gaMeasurementId: string | null;
}

type AnalyticsEnv = {
  VITE_POSTHOG_KEY?: string;
  VITE_POSTHOG_HOST?: string;
  VITE_GA_MEASUREMENT_ID?: string;
};

export const POSTHOG_EU_HOST = "https://eu.i.posthog.com";

/**
 * The analytics this build is set up for, wherever it runs, or `null` for
 * none. Decides whether the cookie banner asks at all: on a preview with the
 * env set it asks, but nothing loads (see `analyticsConfig`).
 */
export function analyticsKeys(env: AnalyticsEnv): AnalyticsConfig | null {
  const posthogKey = env.VITE_POSTHOG_KEY?.trim() || null;
  const ga = env.VITE_GA_MEASUREMENT_ID?.trim() ?? "";
  const gaMeasurementId = /^G-[A-Z0-9]+$/.test(ga) ? ga : null;
  const host = env.VITE_POSTHOG_HOST?.trim().replace(/\/+$/, "") ?? "";
  const posthogHost = /^https:\/\/[a-z0-9.-]+$/i.test(host) ? host : POSTHOG_EU_HOST;
  return posthogKey || gaMeasurementId ? { posthogKey, posthogHost, gaMeasurementId } : null;
}

/** What may load on this host after consent: the keys, on production hosts only. */
export function analyticsConfig(env: AnalyticsEnv, hostname: string): AnalyticsConfig | null {
  return TRACKED_HOSTS.includes(hostname) ? analyticsKeys(env) : null;
}
